import type { FileWithId, NewMediaObject } from '../../lib/store/newMediaStore';

import { useSelector } from '@xstate/store-react';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { uploadFiles } from '../../lib/flotiq-client/api-helpers';
import FlotiqPlugins from '../../lib/flotiq-plugins/flotiqPluginsRegistry';
import { MediaAfterUploadEvent } from '../../lib/flotiq-plugins/plugin-events/MediaAfterUploadEvent';
import { newMediaStore } from '../../lib/store/newMediaStore';

import useSelectedSpace from '../useSelectedSpace';
import useToken from '../useToken';
import { useSpaceConstraint } from './useSpaceConstraint';

export type NewMediaUploadFn = (
  files: null | FileList | File[],
) => Promise<NewMediaObject[] | undefined>;

type HookMediaUploadResult = {
  newMedia: NewMediaObject[];
  onUpload: NewMediaUploadFn;
  removeNewMedia: () => void;
};

const useMediaUpload = () => {
  const jwt = useToken();
  const { t } = useTranslation();
  const { space } = useSelectedSpace();

  const { reload: reloadMediaSumSize } = useSpaceConstraint('media-sum-size');

  const onUpload = useCallback<NewMediaUploadFn>(
    async (files) => {
      if (!jwt || !files) return;

      const filesList = Array.from(files);
      if (!filesList.length) return;

      filesList.forEach(
        (file) => ((file as FileWithId).id = Date.now() + Math.random()),
      );
      const mediaList = filesList.map((file) => ({
        media: file as FileWithId,
        state: 'uploading' as const,
      }));

      newMediaStore.trigger.updateNewMedia({ newMedia: mediaList });
      const media = await uploadFiles(jwt, space, filesList as FileWithId[], t);
      if (media) newMediaStore.trigger.updateNewMedia({ newMedia: media });

      /**
       * @emits FlotiqPlugins."flotiq.media::after-upload"
       */
      media
        ?.filter(({ state }) => state === 'uploaded')
        .forEach(({ media }) =>
          FlotiqPlugins.run(
            'flotiq.media::after-upload',
            new MediaAfterUploadEvent({ media }),
          ),
        );

      reloadMediaSumSize();

      return media;
    },
    [jwt, space, t, reloadMediaSumSize],
  );

  const newMedia = useSelector(
    newMediaStore,
    (state) => state.context.newMedia,
  );

  const removeNewMedia = useCallback(
    () => newMediaStore.trigger.removeNewMedia(),
    [],
  );

  return { newMedia, onUpload, removeNewMedia } as HookMediaUploadResult;
};

export default useMediaUpload;
